import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import PopupTitle from '@App/components/MaplibreMap/PopupTitle';
import theme from '@style/theme.json';
import { useTranslation } from '@utils/i18n/i18n';

import { formatDate } from './microbial-contamination-popup.utils';

import './microbial-contamination-popup.css';

function MicrobiologicalPopupTooltip({ active, payload, label }) {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="microbial-contamination-popup__tooltip">
      <strong>{label}</strong>
      <div className="microbial-contamination-popup__tooltip__values">
        {payload.map(({ dataKey, value, color }) => (
          <div key={dataKey} className="microbial-contamination-popup__tooltip__item">
            <span
              className="microbial-contamination-popup__tooltip__dot"
              style={{ backgroundColor: color }}
            />
            <span>{dataKey === 'ecolli' ? t('E.Coli') : t('Enterococci')}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function MicrobialContaminationPopup({ feature }) {
  const { t } = useTranslation();

  const chartData = (feature?.properties?.contaminant || []).map(entry => ({
    date: formatDate(entry.date),
    ecolli: entry.ecolli,
    enteroc: entry.enteroc,
  }));

  return (
    <div className="microbial-contamination-popup">
      <PopupTitle>
        {t('Beach: ')}
        {feature?.properties?.name}
      </PopupTitle>
      <div className="microbial-contamination-popup__chart">
        {chartData.length > 0
          ? (
              <LineChart height={260} width={480} data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid stroke={theme.color['chart-grid']} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={{ stroke: theme.color.border }}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={{ stroke: theme.color.border }}
                  label={{ value: 'cfu/100ml', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }}
                />
                <Tooltip content={<MicrobiologicalPopupTooltip />} />
                <Line
                  type="monotone"
                  dataKey="ecolli"
                  stroke={theme.color.warning}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }}
                  name="E.Coli"
                />
                <Line
                  type="monotone"
                  dataKey="enteroc"
                  stroke={theme.color.success}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }}
                  name="Enterococci"
                />
              </LineChart>
            )
          : (
              <p className="microbial-contamination-popup__empty">{t('No data available.')}</p>
            )}
      </div>
      <div className="microbial-contamination-popup__legend">
        <div className="microbial-contamination-popup__legend__item">
          <span
            className="microbial-contamination-popup__legend__dot"
            style={{ backgroundColor: theme.color.success }}
          />
          <span>{t('E.Coli')}</span>
        </div>
        <div className="microbial-contamination-popup__legend__item">
          <span
            className="microbial-contamination-popup__legend__dot"
            style={{ backgroundColor: theme.color.warning }}
          />
          <span>{t('Enterococci')}</span>
        </div>
      </div>
    </div>
  );
}

export default MicrobialContaminationPopup;
